import React from "react";
import { useTranslation } from "react-i18next";
import { formatPeriodInterval } from "../../utils/periodBillingCopy";
import Button from "../common/Button";
import { useCurrencyFormatter } from "../../context/RegionalSettingsContext";

/**
 * Professional payment decision modal for period-billing enrollments.
 */
export default function EnrollmentPaymentModal({
  open,
  preview,
  studentName,
  role = "ADMIN",
  onConfirm,
  onCancel,
  onRefreshPreview,
}) {
  const { t } = useTranslation();
  const formatMoney = useCurrencyFormatter();
  const [action, setAction] = React.useState("");
  const [unpaidPriorAck, setUnpaidPriorAck] = React.useState(false);
  const [refreshing, setRefreshing] = React.useState(false);

  React.useEffect(() => {
    if (!open || !preview) return;
    const allowed = preview.allowedPaymentActions || [];
    setAction(allowed[0] || "");
    setUnpaidPriorAck(false);
  }, [open, preview]);

  if (!open || !preview) return null;

  const hasUnpaidPrior = !!preview.unpaidPriorEnrollmentId;
  const needsUnpaidAck = hasUnpaidPrior && !unpaidPriorAck;
  const insufficient = preview.periodBilling && !preview.sufficientForChargeNow;
  const allowed = preview.allowedPaymentActions || [];
  const shortfall = Math.max(0, Number(preview.periodFee || 0) - Number(preview.walletBalance || 0));

  const actionLabels = {
    CHARGE_NOW: t("enrollments.paymentModal.chargeNow"),
    COLLECT_VIA_SCHEDULE: t("enrollments.paymentModal.collectViaSchedule"),
    ENROLL_UNPAID: t("enrollments.paymentModal.enrollUnpaid"),
  };

  const actionHelp = {
    CHARGE_NOW: insufficient
      ? t("enrollments.paymentModal.chargeNowInsufficient")
      : t("enrollments.paymentModal.chargeNowHelp"),
    COLLECT_VIA_SCHEDULE: t("enrollments.paymentModal.collectViaScheduleHelp"),
    ENROLL_UNPAID: t("enrollments.paymentModal.enrollUnpaidHelp"),
  };

  const handleTopUp = () => {
    const roleKey = String(role || "").toUpperCase();
    if (roleKey !== "ADMIN" && roleKey !== "MANAGER") return;
    const base = roleKey === "MANAGER" ? "/manager/advance-payment" : "/admin/advance-payment";
    const amountNeeded = shortfall > 0 ? shortfall : preview.periodFee;
    const url = `${base}?studentId=${preview.studentId}&suggestedAmount=${amountNeeded}`;
    window.open(url, "_blank");
  };

  const canTopUpWallet = ["ADMIN", "MANAGER"].includes(String(role || "").toUpperCase());

  const handleRefresh = async () => {
    if (!onRefreshPreview) return;
    setRefreshing(true);
    try {
      await onRefreshPreview();
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[110] flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-100">
          <h3 className="text-lg font-semibold text-gray-900">{t("enrollments.paymentModal.title")}</h3>
          <p className="text-sm text-gray-600 mt-1">
            {studentName ? t("enrollments.paymentModal.enrolling", { name: studentName }) : t("enrollments.paymentModal.review")}
          </p>
        </div>

        <div className="p-6 space-y-4">
          <div className="rounded-lg bg-blue-50 border border-blue-100 p-4 text-sm text-blue-900 space-y-1">
            <p>
              <span className="font-medium">{t("enrollments.paymentModal.periodFee")}</span>{" "}
              {formatMoney(preview.periodFee)}
            </p>
            <p>
              <span className="font-medium">{t("enrollments.paymentModal.plan")}</span>{" "}
              {formatPeriodInterval(preview.periodInterval) || preview.periodInterval}
            </p>
            <p>
              <span className="font-medium">{t("enrollments.paymentModal.walletBalance")}</span>{" "}
              {formatMoney(preview.walletBalance)}
              {!preview.sufficientForChargeNow && (
                <span className="text-amber-700 ml-1">{t("enrollments.paymentModal.insufficient")}</span>
              )}
            </p>
            {shortfall > 0 && (
              <p>
                <span className="font-medium">{t("enrollments.paymentModal.amountNeeded")}</span>{" "}
                {formatMoney(shortfall)}
              </p>
            )}
          </div>

          {insufficient && (
            <div className="flex flex-wrap gap-2 items-center">
              {canTopUpWallet ? (
                <Button type="button" variant="secondary" onClick={handleTopUp}>
                  {t("enrollments.paymentModal.topUp")}
                </Button>
              ) : (
                <p className="text-sm text-amber-800">
                  {t("enrollments.paymentModal.coachAskTopUp", {
                    defaultValue: "Ask a manager or admin to top up this student’s wallet, then refresh the balance.",
                  })}
                </p>
              )}
              {onRefreshPreview && (
                <Button type="button" variant="secondary" onClick={handleRefresh} disabled={refreshing}>
                  {refreshing ? t("enrollments.paymentModal.refreshing") : t("enrollments.paymentModal.refreshBalance")}
                </Button>
              )}
            </div>
          )}

          {preview.requirePaymentForAttendance && (
            <p className="text-sm text-gray-700 bg-gray-50 border border-gray-200 rounded-lg p-3">
              {t("enrollments.paymentModal.paymentRequired")}
            </p>
          )}

          {hasUnpaidPrior && (
            <div className="rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm text-amber-950">
              <p className="font-semibold">{t("enrollments.paymentModal.unpaidPriorTitle")}</p>
              <p className="mt-1">
                {t("enrollments.paymentModal.unpaidPriorDesc", {
                  id: preview.unpaidPriorEnrollmentId,
                  period: preview.unpaidPriorEnrollmentPeriod,
                  amount: formatMoney(preview.unpaidPriorAmount),
                })}
              </p>
              <label className="flex items-start gap-2 mt-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={unpaidPriorAck}
                  onChange={(e) => setUnpaidPriorAck(e.target.checked)}
                  className="mt-0.5"
                />
                <span>{t("enrollments.paymentModal.unpaidPriorAck")}</span>
              </label>
            </div>
          )}

          {insufficient && (
            <p className="text-sm text-gray-700">
              {t("enrollments.paymentModal.insufficientHelp")}
            </p>
          )}

          <fieldset className="space-y-2">
            <legend className="text-sm font-semibold text-gray-800 mb-2">{t("enrollments.paymentModal.collectLegend")}</legend>
            {allowed.map((key) => (
              <label
                key={key}
                className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer ${
                  action === key ? "border-purple-400 bg-purple-50" : "border-gray-200 hover:bg-gray-50"
                }`}
              >
                <input
                  type="radio"
                  name="periodPaymentAction"
                  value={key}
                  checked={action === key}
                  onChange={() => setAction(key)}
                  className="mt-1"
                />
                <span>
                  <span className="block font-medium text-gray-900">{actionLabels[key] || key}</span>
                  <span className="block text-xs text-gray-600 mt-0.5">{actionHelp[key]}</span>
                </span>
              </label>
            ))}
          </fieldset>
        </div>

        <div className="p-6 border-t border-gray-100 flex justify-end gap-3">
          <Button type="button" variant="secondary" onClick={onCancel}>
            {t("common.cancel")}
          </Button>
          <Button
            type="button"
            onClick={() => onConfirm({ periodPaymentAction: action, confirmUnpaidPrior: hasUnpaidPrior })}
            disabled={!action || needsUnpaidAck}
          >
            {t("enrollments.paymentModal.confirmEnroll")}
          </Button>
        </div>
      </div>
    </div>
  );
}
